import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { categoriesAPI } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Edit2, Trash2, Eye, EyeOff, Filter } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';

const Categories = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { categories: categoryPerms } = usePermissions();

  // Fetch categories with status filter
  const { data: categoriesData, isLoading, error } = useQuery(
    ['categories', statusFilter],
    () => categoriesAPI.getAll({ status: statusFilter }),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        console.error('Categories fetch error:', error);
        toast.error('Failed to load categories');
      }
    }
  );

  // Delete category mutation
  const deleteMutation = useMutation(
    (uuid) => categoriesAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        toast.success('Category deleted successfully!');
        setDeleteModalOpen(false);
        setCategoryToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    (uuid) => categoriesAPI.toggleStatus(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        toast.success('Category status updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update category status');
      },
    }
  );

  const handleEdit = (category) => {
    navigate(`/categories/${category.uuid}/edit`);
  };

  const handleDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteMutation.mutate(categoryToDelete.uuid);
    }
  };

  const handleToggleStatus = (category) => {
    toggleStatusMutation.mutate(category.uuid);
  };

  const handleCreateNew = () => {
    navigate('/categories/new');
  };

  // Extract categories from the correct nested path
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load categories</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Categories</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage product categories and organization
            </p>
          </div>
          {/* Add Category Button - Only show if user can create categories */}
          {categoryPerms.canCreate && (
            <button
              onClick={handleCreateNew}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </button>
          )}
        </div>
      </div>

      {/* Filter Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter by Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="all">All Categories</option>
          </select>
        </div>
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Total: {categories.length} categories
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {categories.map((category, index) => (
          <div
            key={category.uuid}
            className="card-modern group hover-lift animate-fade-in-up"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{category.name}</h3>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  category.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {category.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
              {category.description || 'No description provided'}
            </p>
            
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <span>Products: {category.product_count || 0}</span>
              <span>Created: {new Date(category.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex gap-2 mt-4">
              {/* Edit Button - Only show if user can update categories */}
              {categoryPerms.canUpdate && (
                <button
                  onClick={() => handleEdit(category)}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </button>
              )}
              
              {/* Toggle Status Button - Only show if user can delete categories */}
              {categoryPerms.canDelete && (
                <>
                  {category.is_active ? (
                    <button
                      onClick={() => handleDelete(category)}
                      className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleStatus(category)}
                      className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 text-sm font-medium flex items-center gap-1 transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      Activate
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No categories yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Get started by creating your first category</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Categories; 